import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Plug, Plus, Pencil, Trash2, Search, X, Globe, Hospital, CloudSun, Siren,
  MessageSquare, Newspaper, Map as MapIcon, Zap, CheckCircle2, XCircle, Loader2, Eye, EyeOff,
  Rss, MapPin, AlertTriangle, Send, Layers3, Radio, ShieldCheck, Network,
} from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';
import { distanceLabel, HAZARD_FILTERS } from '../components/externalEvents';
import EventPreviewModal from '../components/EventPreviewModal';
import CriticalAssetsCard from '../components/CriticalAssetsCard';
import RiskEngineSettingsCard from '../components/RiskEngineSettingsCard';
import TrustedSourcesCard from '../components/TrustedSourcesCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

// API Connections — register external platforms DisasterMS integrates with:
// hospital systems (e.g. Epic), weather services, early-warning systems
// (e.g. ncm.gov.sa), social media, breaking news, GIS providers…
// Registered connections are the integration points used for incident
// unified hazard detection, corroboration, incident reporting, map updates and
// GIS layers. Category describes the provider; it does not create a separate
// ForeSight queue.

const CATEGORIES = [
  { value: 'hospital_system', label: 'Hospital System', icon: Hospital, color: 'text-rose-600 bg-rose-50 border-rose-200' },
  { value: 'weather', label: 'Weather', icon: CloudSun, color: 'text-sky-600 bg-sky-50 border-sky-200' },
  { value: 'early_warning', label: 'Early Warning', icon: Siren, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'social_media', label: 'Social Media', icon: MessageSquare, color: 'text-violet-600 bg-violet-50 border-violet-200' },
  { value: 'news', label: 'Breaking News', icon: Newspaper, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { value: 'gis', label: 'GIS / Mapping', icon: MapIcon, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { value: 'sensor', label: 'Sensor / IoT', icon: Radio, color: 'text-cyan-700 bg-cyan-50 border-cyan-200' },
  { value: 'official_feed', label: 'Official Authority', icon: ShieldCheck, color: 'text-blue-700 bg-blue-50 border-blue-200' },
  { value: 'monitoring_service', label: 'Monitoring Service', icon: Rss, color: 'text-teal-700 bg-teal-50 border-teal-200' },
  { value: 'aggregator', label: 'Data Aggregator', icon: Network, color: 'text-slate-700 bg-slate-50 border-slate-200' },
  { value: 'other', label: 'Other', icon: Globe, color: 'text-gray-600 bg-gray-50 border-gray-200' },
];

const AUTH_TYPES = [
  { value: 'none', label: 'No authentication' },
  { value: 'api_key', label: 'API key (custom header)' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'basic', label: 'Basic auth (username / password)' },
];

const ACCESS_METHODS = [
  { value: 'official_api', label: 'API endpoint' },
  { value: 'rss', label: 'RSS / Atom feed' },
  { value: 'public_page', label: 'Public web page' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'manual', label: 'Manual source' },
  { value: 'commercial_provider', label: 'Commercial provider' },
  { value: 'future_connector', label: 'Custom / future connector' },
];

const RESPONSE_FORMATS = [
  { value: 'auto', label: 'Detect automatically' },
  { value: 'json', label: 'JSON' },
  { value: 'geojson', label: 'GeoJSON' },
  { value: 'rss', label: 'RSS' },
  { value: 'atom', label: 'Atom' },
  { value: 'csv', label: 'CSV' },
];

const FIELD_MAPPING_FIELDS = [
  ['external_id', 'External ID', 'id'],
  ['title', 'Title', 'title'],
  ['event_name', 'Event name / subtype', 'event_type'],
  ['description', 'Description', 'description'],
  ['occurred_at', 'Occurred at', 'published_at'],
  ['source_url', 'Source URL', 'url'],
  ['latitude', 'Latitude', 'location.latitude'],
  ['longitude', 'Longitude', 'location.longitude'],
  ['severity', 'Severity', 'severity'],
  ['location_name', 'Location name', 'location.name'],
];

const CONNECTION_SECRET_SENTINEL = '••••••';

const categoryOf = (value) => CATEGORIES.find((c) => c.value === value) || CATEGORIES[CATEGORIES.length - 1];

const inferConnectionCategory = (connection = {}) => {
  const existing = String(connection?.category || '').trim().toLowerCase();
  if (existing && existing !== 'other') return existing;
  const text = [
    connection?.name,
    connection?.base_url,
    connection?.description,
    connection?.origin_key,
  ].filter(Boolean).join(' ').toLowerCase();
  const matches = (terms) => terms.some((term) => text.includes(term));
  if (matches(['twitter', 'x.com', 'mastodon', 'youtube', 'tiktok', 'instagram', 'snapchat', 'social'])) return 'social_media';
  if (matches(['reuters', 'aljazeera', 'arab news', 'arabnews', 'saudi gazette', 'saudigazette', 'newsroom', 'newspaper'])) return 'news';
  if (matches(['weather', 'meteo', 'climate', 'ncm.gov'])) return 'weather';
  if (matches(['ukmto', 'gdacs', 'usgs', 'emsc', 'eonet', 'warning', 'early alert'])) return 'early_warning';
  if (matches(['sensor', 'iot', 'telemetry', 'scada'])) return 'sensor';
  if (matches(['arcgis', 'geospatial', 'mapping', ' gis '])) return 'gis';
  if (matches(['hospital', 'health system', 'emergency department'])) return 'hospital_system';
  if (matches(['gdelt', 'aggregator'])) return 'aggregator';
  if (matches(['authority', '.gov', '.gov.', 'ministry'])) return 'official_feed';
  return 'monitoring_service';
};

export const hazardCoverageForConnection = (connection = {}) => {
  const effective = Array.isArray(connection?.effective_hazard_ids)
    ? connection.effective_hazard_ids
    : [];
  const configured = effective.length > 0
    ? effective
    : Array.isArray(connection?.hazard_ids)
      ? connection.hazard_ids
      : Array.isArray(connection?.hazards) ? connection.hazards : [];
  const haystack = [
    connection?.name,
    connection?.base_url,
    connection?.description,
    connection?.origin_key,
  ].filter(Boolean).join(' ').toLowerCase();
  const inferred = HAZARD_FILTERS
    .filter((hazard) => hazard.feedHints.some((hint) => haystack.includes(hint)))
    .map((hazard) => hazard.id);
  const explicitCoverage = String(connection?.hazard_coverage || '').toLowerCase();
  const explicitlyDisabled = connection?.foresight_enabled === false && !explicitCoverage && configured.length === 0;
  const all = explicitCoverage === 'all';
  const ids = explicitCoverage === 'selected'
    ? [...new Set(configured)]
    : explicitlyDisabled
      ? []
      : [...new Set([...configured, ...inferred])];
  return {
    hazards: HAZARD_FILTERS.filter((hazard) => ids.includes(hazard.id)),
    all,
    inferred: !explicitCoverage && configured.length === 0 && ids.length > 0,
    declared: Boolean(explicitCoverage || configured.length > 0),
  };
};

const blankConnection = () => ({
  name: '', category: 'other', base_url: '', auth_type: 'none',
  api_key: '', api_key_header: 'X-API-Key', username: '', password: '',
  purposes: [], description: '', is_active: true,
  hazard_coverage: 'all', hazard_ids: [], geographic_scope: 'operations_area',
  access_method: 'official_api', response_format: 'auto', items_path: '',
  field_mapping: {},
});

const geographicScopeOf = (connection) =>
  String(connection?.geographic_scope || '').toLowerCase() === 'worldwide'
    ? 'worldwide'
    : 'operations_area';

const normalizeConnectionDraft = (connection = {}) => {
  const connectionFields = { ...(connection || {}) };
  delete connectionFields.foresight_role;
  delete connectionFields.can_detect;
  delete connectionFields.can_corroborate;
  const coverage = hazardCoverageForConnection(connection);
  const configured = Array.isArray(connection?.hazard_ids)
    ? connection.hazard_ids
    : Array.isArray(connection?.hazards) ? connection.hazards : [];
  const inferredIds = coverage.hazards.map((hazard) => hazard.id);
  const explicitCoverage = String(connection?.hazard_coverage || '').toLowerCase();
  return {
    ...blankConnection(),
    ...connectionFields,
    purposes: Array.isArray(connection?.purposes) ? connection.purposes : [],
    field_mapping: connection?.field_mapping && typeof connection.field_mapping === 'object'
      ? { ...connection.field_mapping }
      : {},
    geographic_scope: geographicScopeOf(connection),
    hazard_coverage: explicitCoverage === 'all' || explicitCoverage === 'selected'
      ? explicitCoverage
      : coverage.all ? 'all' : 'selected',
    hazard_ids: explicitCoverage === 'selected' ? configured : coverage.all ? [] : inferredIds,
  };
};

const validHttpEndpoint = (value) => {
  try {
    const parsed = new URL(String(value || '').trim());
    return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.hostname);
  } catch (error) {
    return false;
  }
};

const healthTimestamp = (value = {}) => {
  const raw = (
    value.checked_at
    || value.completed_at
    || value.at
    || value.started_at
  );
  const parsed = Date.parse(raw || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

export const latestConnectionHealthOf = (connection = {}) => {
  const records = [
    connection.last_test
      ? { ...connection.last_test, health_source: 'test' }
      : null,
    connection.last_poll
      ? { ...connection.last_poll, health_source: 'poll' }
      : null,
  ].filter(Boolean);
  if (records.length < 2) return records[0] || null;
  return records.sort(
    (left, right) => healthTimestamp(right) - healthTimestamp(left)
  )[0];
};

export const connectionHealthOf = (connection = {}) => {
  if (connection.is_active === false) return 'disabled';
  const latest = latestConnectionHealthOf(connection);
  if (latest?.ok) {
    // A successful poll has already parsed the source through the ingestion
    // adapter. A legacy transport-only test is merely reachable and must not
    // be counted as operational coverage until parsing is confirmed.
    if (latest.health_source === 'poll' || latest.parse_ok === true) return 'healthy';
    return 'reachable';
  }
  if (latest) return 'failing';
  return 'untested';
};

export const buildHazardCoverageAudit = (
  connections = [],
  hazardDefinitions = HAZARD_FILTERS
) => {
  const enabled = connections.filter((connection) => connection?.is_active !== false);
  const rows = hazardDefinitions.map((hazard) => {
    const sources = enabled.filter((connection) => {
      const coverage = hazardCoverageForConnection(connection);
      return coverage.all || coverage.hazards.some((item) => item.id === hazard.id);
    });
    const health = sources.map(connectionHealthOf);
    return {
      ...hazard,
      sources,
      verified: health.includes('healthy'),
      failingOnly: sources.length > 0 && health.every((value) => value === 'failing'),
      needsTest: (
        sources.length > 0
        && !health.includes('healthy')
        && (health.includes('untested') || health.includes('reachable'))
      ),
    };
  });
  const gaps = rows.filter((row) => row.sources.length === 0);
  return {
    rows,
    gaps,
    covered: rows.length - gaps.length,
    verified: rows.filter((row) => row.verified).length,
    failing: rows.filter((row) => row.failingOnly).length,
    untested: rows.filter((row) => row.needsTest).length,
    enabledSources: enabled.length,
  };
};

const connectionPayload = (form) => {
  const fieldMapping = Object.fromEntries(
    FIELD_MAPPING_FIELDS
      .map(([key]) => [key, String(form.field_mapping?.[key] || '').trim()])
      .filter(([, value]) => Boolean(value))
  );
  const payload = {
    id: form.id,
    name: form.name.trim(),
    category: inferConnectionCategory(form),
    base_url: String(form.base_url || '').trim(),
    auth_type: form.auth_type || 'none',
    api_key_header: String(form.api_key_header || '').trim(),
    username: String(form.username || '').trim(),
    purposes: Array.isArray(form.purposes) ? form.purposes : [],
    description: String(form.description || '').trim(),
    is_active: form.is_active !== false,
    hazard_coverage: form.hazard_coverage,
    hazard_ids: form.hazard_coverage === 'all' ? [] : [...new Set(form.hazard_ids)],
    geographic_scope: geographicScopeOf(form),
    origin_key: form.origin_key,
    provider_key: form.provider_key,
    access_method: form.access_method || 'official_api',
    response_format: form.response_format || 'auto',
    items_path: String(form.items_path || '').trim(),
    field_mapping: fieldMapping,
  };
  // Existing secrets arrive as an opaque server sentinel. Round-trip that
  // sentinel unchanged, or omit an empty edit value, so the browser never
  // replaces a stored credential merely because another field was edited.
  if (!form.id || form.api_key) payload.api_key = form.api_key;
  if (!form.id || form.password) payload.password = form.password;
  return payload;
};

/* ------------------------- Add / Edit modal ------------------------- */
export const ConnectionFormModal = ({ draft, onSave, onClose, onTest }) => {
  const [form, setForm] = useState(() => normalizeConnectionDraft(draft));
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFieldMapping = (key, value) => setForm((current) => ({
    ...current,
    field_mapping: { ...(current.field_mapping || {}), [key]: value },
  }));
  const savedConfiguration = useMemo(
    () => connectionPayload(normalizeConnectionDraft(draft)),
    [draft],
  );
  const hasUnsavedChanges = Boolean(form.id) && (
    JSON.stringify(connectionPayload(form))
    !== JSON.stringify(savedConfiguration)
  );

  const toggleHazard = (value) => setForm((f) => ({
    ...f,
    hazard_ids: f.hazard_ids.includes(value)
      ? f.hazard_ids.filter((hazardId) => hazardId !== value)
      : [...f.hazard_ids, value],
  }));

  const handleSave = async () => {
    if (saving) return;
    if (!form.name?.trim()) {
      setValidationError('Enter a clear source name.');
      return;
    }
    if (!form.id && !form.base_url?.trim()) {
      setValidationError('Enter the source endpoint URL.');
      return;
    }
    if (form.base_url?.trim() && !validHttpEndpoint(form.base_url)) {
      setValidationError('Enter a valid http:// or https:// endpoint URL.');
      return;
    }
    if (form.hazard_coverage === 'selected' && form.hazard_ids.length === 0) {
      setValidationError('Select at least one hazard, or choose All hazards.');
      return;
    }
    const storedApiSecret = form.api_key === CONNECTION_SECRET_SENTINEL;
    const storedPassword = form.password === CONNECTION_SECRET_SENTINEL;
    if (
      ['api_key', 'bearer'].includes(form.auth_type)
      && !form.api_key?.trim()
      && !(form.id && storedApiSecret)
    ) {
      setValidationError('Enter the API key or bearer token for this authentication method.');
      return;
    }
    if (form.auth_type === 'api_key' && !form.api_key_header?.trim()) {
      setValidationError('Enter the API key header name.');
      return;
    }
    if (
      form.auth_type === 'basic'
      && (!form.username?.trim() || (!form.password?.trim() && !(form.id && storedPassword)))
    ) {
      setValidationError('Enter both the username and password for basic authentication.');
      return;
    }
    setValidationError('');
    setSaving(true);
    const payload = connectionPayload(form);
    try { await onSave(payload); } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!form.id || !onTest || testing || hasUnsavedChanges) return;
    setTesting(true);
    try {
      const result = await onTest(form);
      if (result) setForm((current) => ({ ...current, last_test: result }));
    } finally {
      setTesting(false);
    }
  };

  const latestHealth = latestConnectionHealthOf(form);
  const healthState = connectionHealthOf(form);
  const healthLabel = hasUnsavedChanges && latestHealth
    ? 'Configuration changed · save and retest'
    : latestHealth
    ? healthState === 'healthy'
      ? `Ingestion ready${Number.isFinite(Number(latestHealth.event_count)) ? ` · ${Number(latestHealth.event_count)} sample records` : ''}`
      : healthState === 'reachable'
        ? 'Endpoint reachable · parsing not verified'
      : latestHealth.transport_ok && latestHealth.parse_ok === false
        ? 'Adapter issue'
        : 'Connection issue'
    : 'Not tested yet';
  const healthTone = hasUnsavedChanges && latestHealth
    ? 'text-amber-800 bg-amber-50'
    : latestHealth
    ? healthState === 'healthy'
      ? 'text-emerald-700 bg-emerald-50'
      : healthState === 'reachable'
        ? 'text-amber-800 bg-amber-50'
        : 'text-rose-700 bg-rose-50'
    : 'text-slate-600 bg-slate-100';

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto bg-slate-950/55 p-3 backdrop-blur-[2px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      data-testid="api-connection-modal"
    >
      <div className="my-auto w-[720px] max-w-full rounded-2xl bg-slate-50 shadow-2xl ring-1 ring-white/20">
        <div className="flex items-start justify-between gap-3 rounded-t-2xl bg-slate-950 px-4 py-3 text-white">
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-400/15 text-teal-300 ring-1 ring-teal-300/20">
              <Plug className="h-4 w-4" />
            </span>
            <div>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <h3 className="text-sm font-bold">{form.id ? 'Edit API connection' : 'Add API connection'}</h3>
                <p className="text-[9px] font-black uppercase tracking-[0.16em] text-teal-300">ForeSight source registry</p>
              </div>
              <p className="mt-0.5 hidden text-[10px] text-slate-300 sm:block">
                Connect a source and define the hazards it can observe. Its evidence may open a case or strengthen an existing one.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-white/10 hover:text-white" data-testid="api-connection-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-2.5 p-3">
          <section className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-teal-700">1 · Source identity</p>
              <p className="text-[10px] text-slate-500">Identify the provider and endpoint.</p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Name</label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. UKMTO Maritime Warnings" className="mt-1" data-testid="api-connection-name" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Delivery method</label>
                <select
                  value={form.access_method}
                  onChange={(event) => set('access_method', event.target.value)}
                  data-testid="api-connection-access-method"
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2.5 text-xs text-slate-900"
                >
                  {ACCESS_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Endpoint URL {form.id && !form.base_url ? <span className="normal-case text-amber-600">· legacy source has no endpoint</span> : null}
                </label>
                <Input value={form.base_url} onChange={(e) => set('base_url', e.target.value)}
                  placeholder="https://api.example.com/v1" className="mt-1" data-testid="api-connection-url" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Description</label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)}
                  placeholder="What does this source observe, and how authoritative is it?" rows={1} className="mt-1 min-h-[42px] resize-none" />
              </div>
            </div>
          </section>

          <section className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-teal-700">2 · Coverage</p>
              <p className="text-[10px] text-slate-500">Where this source monitors and what it classifies.</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Geographic coverage</p>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-testid="api-connection-geographic-scope">
                  {[
                    {
                      value: 'operations_area',
                      label: 'Operations Area',
                      description: 'Country + radius',
                      icon: MapPin,
                    },
                    {
                      value: 'worldwide',
                      label: 'Worldwide',
                      description: 'Global · e.g. disease',
                      icon: Globe,
                    },
                  ].map((option) => {
                    const ScopeIcon = option.icon;
                    const active = form.geographic_scope === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => set('geographic_scope', option.value)}
                        aria-pressed={active}
                        data-testid={`api-connection-geographic-${option.value}`}
                        className={`flex min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-left transition ${
                          active
                            ? 'bg-slate-950 text-white ring-2 ring-teal-400'
                            : 'bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        <ScopeIcon className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-teal-300' : 'text-teal-700'}`} />
                        <span className="min-w-0">
                          <b className="block truncate text-[11px]">{option.label}</b>
                          <small className={`block truncate text-[9px] ${active ? 'text-slate-300' : 'text-slate-500'}`}>
                            {option.description}
                          </small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="sm:border-l sm:border-slate-100 sm:pl-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Hazard coverage</p>
                  <div className="inline-flex rounded-md bg-slate-100 p-0.5" data-testid="api-connection-coverage">
                    {[
                      ['all', 'All'],
                      ['selected', 'Selected'],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => set('hazard_coverage', value)}
                        aria-pressed={form.hazard_coverage === value}
                        data-testid={`api-connection-coverage-${value}`}
                        className={`rounded px-2 py-1 text-[10px] font-bold ${
                          form.hazard_coverage === value
                            ? 'bg-white text-slate-950 shadow-sm ring-1 ring-slate-200'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.hazard_coverage === 'all' ? (
                  <div className="mt-1.5 flex min-h-[43px] items-center gap-2 rounded-lg bg-teal-50 px-2.5 py-1.5 text-teal-900">
                    <Layers3 className="h-3.5 w-3.5 flex-shrink-0" />
                    <p className="text-[10px]"><b>All {HAZARD_FILTERS.length} hazards</b><br />Classified per observation.</p>
                  </div>
                ) : (
                  <div
                    className="mt-1.5 flex max-h-[82px] flex-wrap gap-1 overflow-y-auto pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    data-testid="api-connection-hazards"
                  >
                    {HAZARD_FILTERS.map((hazard) => {
                      const active = form.hazard_ids.includes(hazard.id);
                      return (
                        <button
                          key={hazard.id}
                          type="button"
                          onClick={() => toggleHazard(hazard.id)}
                          aria-pressed={active}
                          data-testid={`api-connection-hazard-${hazard.id}`}
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition ${
                            active
                              ? 'border-teal-700 bg-teal-700 text-white'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-teal-300 hover:text-teal-800'
                          }`}
                        >
                          <span aria-hidden="true">{hazard.icon}</span> {hazard.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-teal-700">3 · Data adapter</p>
              <p className="text-[10px] text-slate-500">Teach ForeSight how a future source exposes observations.</p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="api-connection-data-adapter">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Response format</label>
                <select
                  value={form.response_format}
                  onChange={(event) => set('response_format', event.target.value)}
                  data-testid="api-connection-response-format"
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2.5 text-xs text-slate-900"
                >
                  {RESPONSE_FORMATS.map((format) => (
                    <option key={format.value} value={format.value}>{format.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Items path</label>
                <Input
                  value={form.items_path}
                  onChange={(event) => set('items_path', event.target.value)}
                  placeholder="e.g. data.events"
                  className="mt-1"
                  data-testid="api-connection-items-path"
                />
              </div>
            </div>
            <details className="mt-2 rounded-lg bg-slate-50 px-2.5 py-2 ring-1 ring-slate-200" data-testid="api-connection-field-mapping">
              <summary className="cursor-pointer text-[10px] font-bold text-slate-700">
                Advanced field mapping <span className="font-normal text-slate-500">· dot paths or CSV column names</span>
              </summary>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {FIELD_MAPPING_FIELDS.map(([key, label, placeholder]) => (
                  <div key={key}>
                    <label className="text-[9px] font-bold uppercase tracking-wide text-slate-500">{label}</label>
                    <Input
                      value={form.field_mapping?.[key] || ''}
                      onChange={(event) => setFieldMapping(key, event.target.value)}
                      placeholder={placeholder}
                      className="mt-1 h-8 bg-white text-[11px]"
                      data-testid={`api-connection-field-${key}`}
                    />
                  </div>
                ))}
              </div>
            </details>
          </section>

          <section className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
            <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-teal-700">4 · Authentication & health</p>
              <p className="text-[10px] text-slate-500">Credentials are protected; health updates after tests and polls.</p>
            </div>
            <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[minmax(220px,1fr)_auto]">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Authentication</label>
                <select value={form.auth_type} onChange={(e) => set('auth_type', e.target.value)} data-testid="api-connection-auth"
                  className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2.5 text-xs text-slate-900">
                  {AUTH_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <div className="flex h-9 flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-2.5 ring-1 ring-slate-200">
                <label className="flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-slate-800">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)}
                    className="accent-teal-700" data-testid="api-connection-active" />
                  Enabled
                </label>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${healthTone}`} data-testid="api-connection-health">
                  {healthLabel}
                </span>
                {form.id && onTest ? (
                  <button
                    type="button"
                    onClick={handleTest}
                    disabled={testing || hasUnsavedChanges}
                    title={
                      hasUnsavedChanges
                        ? 'Save configuration changes before testing this source.'
                        : 'Test transport and parse a sample response.'
                    }
                    className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[9px] font-bold text-teal-800 ring-1 ring-slate-200 hover:bg-teal-50 disabled:opacity-60"
                    data-testid="api-connection-form-test"
                  >
                    {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                    {testing ? 'Testing…' : hasUnsavedChanges ? 'Save to test' : 'Test'}
                  </button>
                ) : !form.id ? (
                  <small className="whitespace-nowrap text-[9px] text-slate-400">Save to test</small>
                ) : null}
              </div>
            </div>

            {(form.auth_type === 'api_key' || form.auth_type === 'bearer') && (
              <div className="mt-2 grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-2 ring-1 ring-slate-200 sm:grid-cols-2">
                {form.auth_type === 'api_key' && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Header name</label>
                    <Input value={form.api_key_header} onChange={(e) => set('api_key_header', e.target.value)}
                      placeholder="X-API-Key" className="mt-1 bg-white" data-testid="api-connection-key-header" />
                  </div>
                )}
                <div className={form.auth_type === 'bearer' ? 'col-span-2' : ''}>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {form.auth_type === 'bearer' ? 'Bearer token' : 'API key'}
                    {form.api_key === CONNECTION_SECRET_SENTINEL ? <span className="normal-case text-emerald-700"> · stored</span> : null}
                  </label>
                  <div className="relative mt-1">
                    <Input type={showSecret ? 'text' : 'password'} value={form.api_key} onChange={(e) => set('api_key', e.target.value)}
                      placeholder={form.id ? 'Leave unchanged to keep the stored secret' : 'secret…'} className="bg-white pr-9" data-testid="api-connection-key" />
                    <button type="button" onClick={() => setShowSecret((v) => !v)}
                      disabled={form.api_key === CONNECTION_SECRET_SENTINEL}
                      aria-label={showSecret ? 'Hide credential' : 'Show credential'}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-40">
                      {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {form.auth_type === 'basic' && (
              <div className="mt-2 grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-2 ring-1 ring-slate-200 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Username</label>
                  <Input value={form.username} onChange={(e) => set('username', e.target.value)} className="mt-1 bg-white" data-testid="api-connection-username" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Password
                    {form.password === CONNECTION_SECRET_SENTINEL ? <span className="normal-case text-emerald-700"> · stored</span> : null}
                  </label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    placeholder={form.id ? 'Leave unchanged to keep the stored secret' : ''}
                    className="mt-1 bg-white"
                    data-testid="api-connection-password"
                  />
                </div>
              </div>
            )}

          </section>

          {validationError && (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" role="alert">
              {validationError}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" onClick={onClose} data-testid="api-connection-cancel">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="api-connection-save">
              {saving ? 'Saving…' : form.id ? 'Save connection' : 'Add connection'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* --------------------------- Operations Area --------------------------- */
// The single source of truth for local monitoring: a country + radius.
// Connections may follow this boundary or explicitly monitor worldwide.
export const OperationsAreaCard = () => {
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const latestRequest = useRef(0);

  const loadArea = useCallback(async ({ reset = false } = {}) => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    if (reset) setArea(null);
    setError('');
    setLoading(true);
    try {
      const res = await CommanServices.getOperationsArea();
      if (latestRequest.current !== requestId) return;
      if (res?.status && res.data) {
        setArea(res.data);
      } else {
        setArea(null);
        setError(res?.message || 'The Operations Area could not be loaded.');
      }
    } catch (e) {
      if (latestRequest.current !== requestId) return;
      setArea(null);
      setError('The Operations Area could not be loaded.');
    } finally {
      if (latestRequest.current === requestId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArea();
    const refresh = () => loadArea();
    const refreshForSession = () => loadArea({ reset: true });
    window.addEventListener('focus', refresh);
    window.addEventListener('operations-area-changed', refresh);
    window.addEventListener('disasterms:saas-institutions-changed', refresh);
    window.addEventListener('saas-session-changed', refreshForSession);
    return () => {
      latestRequest.current += 1;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('operations-area-changed', refresh);
      window.removeEventListener('disasterms:saas-institutions-changed', refresh);
      window.removeEventListener('saas-session-changed', refreshForSession);
    };
  }, [loadArea]);

  const radiusKm = Number(area?.radius_km);
  const radiusLabel = Number.isFinite(radiusKm) ? `${radiusKm.toLocaleString()} km` : 'Not configured';
  const countryLabel = area?.country_name || area?.country_iso2 || 'Not configured';

  return (
    <div className="bg-white rounded-xl border border-indigo-200 shadow-sm p-4 mb-5" data-testid="operations-area-card">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
          <Globe className="h-4 w-4" />
        </span>
        <div>
          <h3 className="text-sm font-bold text-gray-900 leading-tight">Operations Area</h3>
          <p className="text-[11px] text-gray-500">
            {area ? `Active: ${countryLabel} · ${radiusLabel} radius` : 'SaaS-managed institution scope'}
          </p>
        </div>
      </div>

      {loading && !area ? (
        <p className="text-xs text-gray-500" role="status">Loading the institution Operations Area…</p>
      ) : error ? (
        <p className="text-xs font-medium text-rose-600" role="alert">{error}</p>
      ) : (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="operations-area-readonly-values">
          <div className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-2.5">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Country</dt>
            <dd className="mt-1 text-sm font-semibold text-gray-900" data-testid="operations-area-country">{countryLabel}</dd>
          </div>
          <div className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-2.5">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Watch radius</dt>
            <dd className="mt-1 text-sm font-semibold text-indigo-700" data-testid="operations-area-radius">{radiusLabel}</dd>
          </div>
        </dl>
      )}

      <p className="mt-3 text-[11px] text-gray-400">
        This area is managed by the SaaS account and is read-only here.{' '}
        Connections using <span className="font-semibold text-gray-500">Operations Area</span> follow this country and watch radius
        automatically; Worldwide connections remain global for cross-border threats such as infectious disease.
      </p>
    </div>
  );
};

export const HazardCoverageAuditCard = ({
  connections = [],
  loading = false,
  error = '',
  essentialBaseline = null,
  essentialCoverage = null,
  provisioning = false,
  provisionResult = null,
  provisionError = '',
  onProvision,
  onAddHazard,
}) => {
  const audit = buildHazardCoverageAudit(connections);
  const essentialSummary = essentialCoverage?.summary || {};
  const authoritativeTotal = Number(essentialSummary.hazard_count);
  const authoritativeConfigured = Number(essentialSummary.configured_coverage_count);
  const authoritativeOperational = Number(essentialSummary.operationally_covered_count);
  const hasEssentialAudit = (
    Number.isFinite(authoritativeTotal)
    && Number.isFinite(authoritativeConfigured)
    && Number.isFinite(authoritativeOperational)
    && Array.isArray(essentialCoverage?.hazards)
  );
  const definitionsById = new Map(audit.rows.map((hazard) => [hazard.id, hazard]));
  const essentialGaps = hasEssentialAudit
    ? essentialCoverage.hazards
      .filter((hazard) => hazard?.configured_coverage !== true)
      .map((hazard) => ({
        ...(definitionsById.get(String(hazard?.hazard_id || '')) || {}),
        id: String(hazard?.hazard_id || ''),
        label: (
          definitionsById.get(String(hazard?.hazard_id || ''))?.label
          || hazard?.name
          || hazard?.hazard_id
        ),
        specialistGap: hazard?.specialist_source_gap === true,
        broadGap: hazard?.broad_source_gap === true,
      }))
      .filter((hazard) => hazard.id)
    : audit.gaps;
  const total = hasEssentialAudit ? authoritativeTotal : audit.rows.length;
  const covered = hasEssentialAudit ? authoritativeConfigured : audit.covered;
  const operational = hasEssentialAudit ? authoritativeOperational : audit.verified;
  const configuredComplete = !loading && !error && total > 0 && covered === total;
  const operationalComplete = configuredComplete && operational === total;
  const operationalGaps = hasEssentialAudit
    ? essentialCoverage.hazards
      .filter((hazard) => (
        hazard?.configured_coverage === true
        && hazard?.operationally_covered !== true
      ))
      .map((hazard) => ({
        ...(definitionsById.get(String(hazard?.hazard_id || '')) || {}),
        ...hazard,
        id: String(hazard?.hazard_id || ''),
        label: (
          definitionsById.get(String(hazard?.hazard_id || ''))?.label
          || hazard?.name
          || hazard?.hazard_id
        ),
      }))
      .filter((hazard) => hazard.id)
    : [];
  const sourceLabelsByKey = new Map();
  connections.forEach((connection) => {
    const label = connection?.name || connection?.provider_key || connection?.id;
    [connection?.provider_key, connection?.origin_key, connection?.id]
      .filter(Boolean)
      .forEach((key) => sourceLabelsByKey.set(String(key), String(label)));
  });
  const sourceLabels = (keys = []) => [...new Set(
    (Array.isArray(keys) ? keys : [])
      .filter(Boolean)
      .map((key) => sourceLabelsByKey.get(String(key)) || String(key))
  )];
  const degradedSources = sourceLabels(
    operationalGaps.flatMap((hazard) => hazard?.degraded_source_keys || [])
  );
  const unverifiedSources = sourceLabels(
    operationalGaps.flatMap((hazard) => hazard?.unverified_source_keys || [])
  );
  const provisioningData = provisionResult?.provisioning || {};
  const created = Array.isArray(provisioningData.created_provider_keys)
    ? provisioningData.created_provider_keys
    : [];
  const updated = Array.isArray(provisioningData.updated_provider_keys)
    ? provisioningData.updated_provider_keys
    : [];
  const unchanged = Array.isArray(provisioningData.unchanged_provider_keys)
    ? provisioningData.unchanged_provider_keys
    : [];
  const skipped = Array.isArray(provisioningData.skipped)
    ? provisioningData.skipped
    : [];

  return (
    <section
      className={`mb-5 rounded-xl border p-4 shadow-sm ${
        operationalComplete
          ? 'border-emerald-200 bg-emerald-50'
          : configuredComplete
            ? 'border-amber-200 bg-amber-50'
            : 'border-rose-200 bg-white'
      }`}
      data-testid="hazard-coverage-audit"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
            operationalComplete
              ? 'bg-emerald-600 text-white'
              : configuredComplete
                ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-200'
                : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
          }`}>
            {operationalComplete ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          </span>
          <div>
            <h3 className="text-sm font-bold text-slate-900">ForeSight {total}-hazard coverage audit</h3>
            <p className="mt-0.5 text-[11px] text-slate-600">
              {error
                ? 'Coverage could not be audited until the connection registry reloads.'
                : loading
                ? 'Checking enabled source declarations…'
                : configuredComplete
                  ? hasEssentialAudit
                    ? operationalComplete
                      ? `All ${total} hazards are configured and have a successful test or poll.`
                      : `All ${total} hazards are configured; ${operational}/${total} currently have a successful test or poll.`
                    : `All ${total} hazards have at least one enabled source.`
                  : `${essentialGaps.length} coverage gap${essentialGaps.length === 1 ? '' : 's'} require source activation.`}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {onProvision && (
            <button
              type="button"
              onClick={onProvision}
              disabled={provisioning || loading}
              aria-busy={provisioning}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-950 px-3 py-2 text-[10px] font-bold text-white hover:bg-teal-900 disabled:cursor-wait disabled:opacity-60"
              data-testid="activate-essential-coverage"
            >
              {provisioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
              {provisioning
                ? 'Activating…'
                : configuredComplete
                  ? 'Reconcile essential coverage'
                  : 'Activate essential coverage'}
            </button>
          )}
          {essentialBaseline?.version && (
            <span className="text-[9px] font-semibold text-slate-400">
              Essential baseline {essentialBaseline.version}
            </span>
          )}
          <div className="flex flex-wrap justify-end gap-1.5 text-[10px] font-bold">
          <span
            className={`rounded-full border px-2 py-1 ${
              configuredComplete
                ? 'border-sky-200 bg-white text-sky-800'
                : 'border-rose-200 bg-rose-50 text-rose-800'
            }`}
            data-testid="hazard-coverage-count"
          >
            {loading || error ? '—' : covered}/{total} {hasEssentialAudit ? 'configured' : 'covered'}
          </span>
          {!loading && !error && hasEssentialAudit && (
            <span className={`rounded-full border px-2 py-1 ${
              operationalComplete
                ? 'border-emerald-200 bg-white text-emerald-800'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`} data-testid="hazard-operational-count">
              {operational}/{total} successful test/poll
            </span>
          )}
          {!loading && !error && (
            <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-slate-700">
              {audit.enabledSources} enabled source{audit.enabledSources === 1 ? '' : 's'}
            </span>
          )}
          {!loading && !error && !hasEssentialAudit && audit.verified > 0 && (
            <span className="rounded-full border border-emerald-200 bg-white px-2 py-1 text-emerald-700">
              {audit.verified} verified
            </span>
          )}
          {!loading && !error && !hasEssentialAudit && audit.untested > 0 && (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800">
              {audit.untested} need testing
            </span>
          )}
          {!loading && !error && !hasEssentialAudit && audit.failing > 0 && (
            <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-1 text-rose-800">
              {audit.failing} failing
            </span>
          )}
          </div>
        </div>
      </div>

      {!loading && !error && essentialGaps.length > 0 && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2.5" data-testid="hazard-coverage-gaps">
          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-800">
            {hasEssentialAudit
              ? 'Essential coverage gaps · specialist and/or broad source missing'
              : 'Coverage gaps · no enabled declared source'}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {essentialGaps.map((hazard) => (
              <button
                key={hazard.id}
                type="button"
                onClick={() => onAddHazard?.(hazard)}
                className="rounded-full border border-rose-200 bg-white px-2 py-1 text-[10px] font-semibold text-rose-800 hover:bg-rose-100"
                data-testid={`hazard-coverage-gap-${hazard.id}`}
                title={`Add a source prefilled for ${hazard.label}`}
              >
                {hazard.icon} {hazard.label}
                {hazard.specialistGap ? ' · specialist' : ''}
                {hazard.broadGap ? ' · broad' : ''}
                {' '}<Plus className="ml-0.5 inline h-3 w-3" />
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && !error && operationalGaps.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-white/80 p-2.5" data-testid="hazard-operational-gaps">
          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-amber-900">
            Operational health gaps · configured, but not ingestion-ready
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {operationalGaps.map((hazard) => {
              const missing = [
                hazard?.specialist_health_gap === true ? 'specialist' : '',
                hazard?.broad_health_gap === true ? 'broad monitor' : '',
              ].filter(Boolean);
              return (
                <span
                  key={hazard.id}
                  className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-900"
                  data-testid={`hazard-operational-gap-${hazard.id}`}
                >
                  {hazard.icon} {hazard.label}
                  {missing.length ? ` · ${missing.join(' + ')}` : ''}
                </span>
              );
            })}
          </div>
          {(degradedSources.length > 0 || unverifiedSources.length > 0) && (
            <div className="mt-2 space-y-0.5 text-[9px] leading-relaxed text-slate-600">
              {degradedSources.length > 0 && (
                <p data-testid="degraded-essential-sources">
                  <b className="text-rose-700">Failed test/poll:</b> {degradedSources.join(', ')}
                </p>
              )}
              {unverifiedSources.length > 0 && (
                <p data-testid="unverified-essential-sources">
                  <b className="text-amber-800">Awaiting successful test/poll:</b> {unverifiedSources.join(', ')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {provisionResult && (
        <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-2.5" data-testid="essential-coverage-result">
          <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-bold">
            <span className="mr-1 text-sky-950">Essential registry reconciled:</span>
            <span className="rounded-full border border-sky-200 bg-white px-2 py-0.5 text-sky-800">
              {created.length} created{created.length ? ` · ${created.join(', ')}` : ''}
            </span>
            <span className="rounded-full border border-sky-200 bg-white px-2 py-0.5 text-sky-800">
              {updated.length} updated{updated.length ? ` · ${updated.join(', ')}` : ''}
            </span>
            <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-700">
              {unchanged.length} unchanged{unchanged.length ? ` · ${unchanged.join(', ')}` : ''}
            </span>
            <span className={`rounded-full border bg-white px-2 py-0.5 ${
              skipped.length ? 'border-amber-200 text-amber-800' : 'border-slate-200 text-slate-700'
            }`}>
              {skipped.length} skipped
            </span>
          </div>
          {skipped.length > 0 && (
            <ul className="mt-2 space-y-1 text-[9px] text-amber-800">
              {skipped.map((item, index) => (
                <li key={`${item?.provider_key || 'provider'}-${index}`}>
                  <b>{item?.provider_key || 'Provider'}:</b> {item?.detail || 'Not provisioned.'}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[9px] font-semibold text-sky-900">
            Reconciliation changes configuration only. A source and its hazards remain health-unverified until a successful ingestion test or poll.
          </p>
        </div>
      )}

      {provisionError && (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-2 text-[10px] font-semibold text-rose-800" role="alert" data-testid="essential-coverage-error">
          {provisionError}
        </p>
      )}

      {error && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[10px] font-semibold text-amber-800" role="alert">
          {error}
        </p>
      )}

      <p className="mt-2 text-[10px] leading-relaxed text-slate-500">
        {hasEssentialAudit
          ? 'Essential coverage requires specialist and broad-monitor breadth for each hazard; these are source-coverage descriptions, not fixed processing roles. '
          : 'Coverage counts only enabled connections with explicit or recognized hazard coverage. '}
        A source marked “all” covers the full library; selected sources cover only their saved hazard IDs.
        Health badges remain separate so configured coverage is never mistaken for a successful live test.
      </p>
    </section>
  );
};

/* ---------------------- Live events → incidents ---------------------- */
// Lists events pulled from a connected open feed (USGS, EONET…) and reports
// the chosen one as a REAL incident through the normal incident pipeline.
const SEVERITY_BADGE = {
  High: 'text-rose-700 bg-rose-50 border-rose-200',
  Medium: 'text-orange-700 bg-orange-50 border-orange-200',
  Low: 'text-yellow-700 bg-yellow-50 border-yellow-200',
};

const FeedEventsModal = ({ connection, events: initialEvents, onClose }) => {
  const [events] = useState(initialEvents);
  // Reporting incidents from feeds is retired — events are dispatched as
  // warnings instead (the preview modal handles it).
  const [preview, setPreview] = useState(null);

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="feed-events-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[620px] max-h-[85vh] flex flex-col p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-bold text-gray-900">Live events — {connection.name}</h3>
          <button onClick={onClose} data-testid="feed-events-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <p className="text-[11px] text-gray-500 mb-3">
          {events.length} event{events.length === 1 ? '' : 's'} fetched from the feed. Click <span className="font-semibold">Dispatch</span> to preview an event and send a warning to all app users.
        </p>
        <div className="flex-1 overflow-y-auto space-y-2" data-testid="feed-events-list">
          {events.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">The feed returned no events right now.</p>
          )}
          {events.map((ev) => (
            <div key={ev.external_id} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-slate-50/50 px-3 py-2.5" data-testid={`feed-event-${ev.external_id}`}>
              <span className={`mt-0.5 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold flex-shrink-0 ${SEVERITY_BADGE[ev.severity] || SEVERITY_BADGE.Medium}`}>
                <AlertTriangle className="h-3 w-3" /> {ev.severity}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 leading-snug">{ev.title}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {ev.occurred_at ? new Date(ev.occurred_at).toLocaleString() : 'Time unknown'}
                  {ev.gps && (
                    <span className={`ml-2 inline-flex items-center gap-0.5 ${ev.in_aoi ? 'font-bold text-indigo-600' : ''}`}>
                      <MapPin className="h-3 w-3" /> {distanceLabel(ev)}
                    </span>
                  )}
                </p>
              </div>
              <button
                data-testid={`feed-event-dispatch-${ev.external_id}`}
                onClick={() => setPreview({ ...ev, connection: { name: connection.name, category: connection.category, id: connection.id } })}
                className="flex items-center gap-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold px-2.5 py-1.5 flex-shrink-0"
              >
                <Send className="h-3.5 w-3.5" />
                Dispatch
              </button>
            </div>
          ))}
        </div>
      </div>
      {preview && <EventPreviewModal event={preview} onClose={() => setPreview(null)} />}
    </div>,
    document.body
  );
};

/* ------------------------------- Page ------------------------------- */
const ApiConnections = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [signalCatalog, setSignalCatalog] = useState(null);
  const [provisioningEssential, setProvisioningEssential] = useState(false);
  const [essentialProvisionResult, setEssentialProvisionResult] = useState(null);
  const [essentialProvisionError, setEssentialProvisionError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [draft, setDraft] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [fetchingId, setFetchingId] = useState(null);
  const [eventsModal, setEventsModal] = useState(null); // { connection, events }
  const prefillConsumed = useRef(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const [connectionsResult, catalogResult] = await Promise.allSettled([
        CommanServices.getApiConnections(),
        typeof CommanServices.getSignalCatalog === 'function'
          ? CommanServices.getSignalCatalog()
          : Promise.resolve(null),
      ]);
      const connectionsResponse = connectionsResult.status === 'fulfilled'
        ? connectionsResult.value
        : null;
      if (connectionsResponse?.status && Array.isArray(connectionsResponse.data)) {
        setItems(connectionsResponse.data);
      } else {
        setItems([]);
        setLoadError(connectionsResponse?.message || 'The connection registry could not be loaded.');
      }
      const catalogResponse = catalogResult.status === 'fulfilled'
        ? catalogResult.value
        : null;
      setSignalCatalog(catalogResponse?.status ? catalogResponse.data || null : null);
    } catch (e) {
      setItems([]);
      setSignalCatalog(null);
      setLoadError('The connection registry could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (prefillConsumed.current || typeof window === 'undefined') return;
    prefillConsumed.current = true;
    const params = new URLSearchParams(window.location.search);
    const hazardId = String(params.get('hazard') || '').trim().toLowerCase();
    const validHazard = HAZARD_FILTERS.some((hazard) => hazard.id === hazardId);
    if (!validHazard) return;

    setDraft({
      ...blankConnection(),
      hazard_coverage: 'selected',
      hazard_ids: [hazardId],
      geographic_scope: geographicScopeOf({
        geographic_scope: params.get('geographic_scope') || params.get('scope'),
      }),
    });

    params.delete('hazard');
    params.delete('geographic_scope');
    params.delete('scope');
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`
    );
  }, []);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) =>
      String(c.name || '').toLowerCase().includes(q) ||
      String(c.base_url || '').toLowerCase().includes(q) ||
      categoryOf(c.category).label.toLowerCase().includes(q)
    );
  }, [items, searchTerm]);

  const provisionEssentialCoverage = async () => {
    if (provisioningEssential) return;
    setProvisioningEssential(true);
    setEssentialProvisionResult(null);
    setEssentialProvisionError('');
    try {
      const response = await CommanServices.provisionEssentialForesightCoverage();
      if (!response?.status) {
        throw new Error(response?.message || 'Essential coverage could not be activated.');
      }
      const result = response.data || {};
      setEssentialProvisionResult(result);
      await load();
      const changes = result.provisioning || {};
      const count = (value) => Array.isArray(value) ? value.length : 0;
      toast.success(
        `Essential coverage reconciled: ${count(changes.created_provider_keys)} created, `
        + `${count(changes.updated_provider_keys)} updated, `
        + `${count(changes.unchanged_provider_keys)} unchanged, `
        + `${count(changes.skipped)} skipped.`
      );
    } catch (error) {
      const message = error?.message || 'Essential coverage could not be activated.';
      setEssentialProvisionError(message);
      toast.error(message);
    } finally {
      setProvisioningEssential(false);
    }
  };

  const save = async (form) => {
    if (!form.name?.trim()) { toast.error('Connection name is required'); return; }
    const res = form.id
      ? await CommanServices.updateApiConnection(form.id, form)
      : await CommanServices.addApiConnection(form);
    if (!res?.status) { toast.error(res?.message || 'Failed to save connection'); return; }
    setDraft(null);
    toast.success(form.id ? 'Connection updated' : 'Connection added');
    await load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete connection "${c.name}"?`)) return;
    const res = await CommanServices.deleteApiConnection(c.id);
    if (!res?.status) { toast.error(res?.message || 'Failed to delete connection'); return; }
    toast.success('Connection deleted');
    await load();
  };

  const test = async (c) => {
    setTestingId(c.id);
    try {
      const res = await CommanServices.testApiConnection(c.id);
      const r = res?.status ? res.data : null;
      if (r?.ok) {
        const records = Number.isFinite(Number(r.event_count))
          ? ` · ${Number(r.event_count)} sample records`
          : '';
        const http = r.status_code ? ` · HTTP ${r.status_code}` : '';
        const latency = Number.isFinite(Number(r.latency_ms)) ? ` in ${Number(r.latency_ms)} ms` : '';
        toast.success(
          r.parse_ok === true
            ? `"${c.name}" ingestion ready${records}${http}${latency}`
            : `"${c.name}" reachable${http}${latency}`
        );
      } else {
        const failure = r?.transport_ok && r?.parse_ok === false
          ? 'endpoint reachable, but the response could not be parsed'
          : 'test failed';
        toast.error(`"${c.name}" ${failure}${r?.error ? ` — ${r.error}` : r?.status_code ? ` — HTTP ${r.status_code}` : ''}`);
      }
      await load();
      return r;
    } finally {
      setTestingId(null);
    }
  };

  // Pull live events from an incident-reporting feed (USGS, EONET…)
  const fetchEvents = async (c) => {
    setFetchingId(c.id);
    try {
      const res = await CommanServices.fetchApiConnectionEvents(c.id);
      if (!res?.status) { toast.error(res?.message || 'Failed to fetch events from the feed'); return; }
      setEventsModal({ connection: c, events: res.data?.events || [] });
    } finally {
      setFetchingId(null);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-1 overflow-hidden">
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">API Connections</h2>
          <p className="text-sm text-gray-600 mb-4">
            The connection registry — every API (manual or SuperConnect) is stored, tested and health-checked here,
            with Operations Area or Worldwide coverage. Any connected source can open a detected-hazard case or
            strengthen an existing case; provider categories remain source metadata inside one evidence trail.
          </p>

          <OperationsAreaCard />
          <TrustedSourcesCard />
          <CriticalAssetsCard />
          <RiskEngineSettingsCard />
          <HazardCoverageAuditCard
            connections={items}
            loading={loading}
            error={loadError}
            essentialBaseline={signalCatalog?.essential_baseline}
            essentialCoverage={signalCatalog?.essential_coverage}
            provisioning={provisioningEssential}
            provisionResult={essentialProvisionResult}
            provisionError={essentialProvisionError}
            onProvision={provisionEssentialCoverage}
            onAddHazard={(hazard) => setDraft({
              ...blankConnection(),
              hazard_coverage: 'selected',
              hazard_ids: [hazard.id],
            })}
          />

          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                data-testid="api-connections-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search connections…"
                className="w-full text-sm border border-gray-300 rounded-lg bg-white pl-9 pr-3 py-2"
              />
            </div>
            <button
              data-testid="add-api-connection-btn"
              onClick={() => setDraft(blankConnection())}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold px-3.5 py-2.5 hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add Connection
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-sm text-gray-400" data-testid="api-connections-loading">Loading connections…</div>
        ) : loadError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-5 text-center shadow-sm" data-testid="api-connections-error">
            <AlertTriangle className="mx-auto h-6 w-6 text-amber-700" />
            <p className="mt-2 text-sm font-semibold text-amber-900">{loadError}</p>
            <button
              type="button"
              onClick={() => {
                setLoading(true);
                load();
              }}
              className="mt-3 rounded-lg bg-slate-950 px-3 py-2 text-xs font-bold text-white"
            >
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-14" data-testid="api-connections-empty">
            <Plug className="h-9 w-9 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 font-medium">
              {searchTerm ? 'No connections match your search' : 'No API connections yet'}
            </p>
            {!searchTerm && (
              <p className="text-xs text-gray-400 mt-1 max-w-md mx-auto">
                Add a connection to a hospital system, weather service, early-warning platform, social media,
                news feed or GIS provider to start detecting hazards and adding relevant evidence.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {[
              {
                id: 'hazard-sources',
                label: 'Hazard Detection & Evidence Sources',
                match: () => true,
              },
            ].map((group) => {
              const groupItems = filtered.filter(group.match);
              if (groupItems.length === 0) return null;
              return (
                <div key={group.id} data-testid={`connection-group-${group.id}`}>
                  <h4 className="text-[11px] font-black uppercase tracking-widest text-gray-500 mb-2">{group.label}</h4>
                  <div className="space-y-3">
                    {groupItems.map((c) => {
              const cat = categoryOf(c.category);
                      const CatIcon = cat.icon;
                      const lp = c.last_poll;
                      const latestHealth = latestConnectionHealthOf(c);
                      const coverage = hazardCoverageForConnection(c);
              const geographicScope = geographicScopeOf(c);
              const health = connectionHealthOf(c);
              const authLabel = AUTH_TYPES.find((item) => item.value === c.auth_type)?.label || 'No authentication';
              const accessLabel = ACCESS_METHODS.find((item) => item.value === c.access_method)?.label || 'API endpoint';
              const responseLabel = RESPONSE_FORMATS.find((item) => item.value === c.response_format)?.label || 'Detect automatically';
              return (
                <div key={c.id} className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-start gap-4" data-testid={`api-connection-row-${c.id}`}>
                  <span className={`flex h-10 w-10 items-center justify-center rounded-lg border flex-shrink-0 ${cat.color}`}>
                    <CatIcon className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-gray-900">{c.name}</h3>
                      <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold ${cat.color}`}>{cat.label}</span>
                      {c.is_active === false && (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">Disabled</span>
                      )}
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold ${
                          health === 'healthy'
                            ? 'text-emerald-600'
                            : health === 'failing'
                              ? 'text-rose-600'
                              : health === 'reachable'
                                ? 'text-amber-700'
                                : 'text-slate-500'
                        }`}
                        title={latestHealth?.error || ''}
                        data-testid={`api-connection-status-${c.id}`}
                      >
                        {health === 'healthy'
                          ? <CheckCircle2 className="h-3 w-3" />
                          : health === 'failing'
                            ? <XCircle className="h-3 w-3" />
                            : <Zap className="h-3 w-3" />}
                        {health === 'healthy'
                          ? latestHealth?.parse_ok === true
                            ? `Ingestion ready${Number.isFinite(Number(latestHealth.event_count)) ? ` · ${Number(latestHealth.event_count)} sample records` : ''}`
                            : latestHealth?.status_code ? `Reachable (HTTP ${latestHealth.status_code})` : 'Healthy'
                          : health === 'failing'
                            ? latestHealth?.transport_ok && latestHealth?.parse_ok === false
                              ? 'Adapter issue'
                              : 'Connection issue'
                            : health === 'reachable'
                              ? 'Endpoint reachable · parsing not verified'
                            : health === 'disabled'
                              ? 'Health paused'
                              : 'Not tested'}
                      </span>
                      {lp && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${lp.ok ? 'text-sky-600' : 'text-rose-600'}`} title={lp.error || (lp.at ? `Last poll ${new Date(lp.at).toLocaleString()}` : '')} data-testid={`api-connection-poll-${c.id}`}>
                          <Rss className="h-3 w-3" />
                          {lp.ok ? `Polled · ${lp.events ?? 0} events` : 'Poll failed'}
                        </span>
                      )}
                    </div>
                    {c.base_url ? (
                      <p className="mt-0.5 truncate text-[11px] text-gray-500" data-testid={`connection-endpoint-${c.id}`}>{c.base_url}</p>
                    ) : (
                      <p className="mt-0.5 text-[10px] font-semibold text-amber-700" data-testid={`connection-endpoint-${c.id}`}>
                        No endpoint configured · legacy/manual connection
                      </p>
                    )}
                    {c.created_at && (
                      <p className="text-[10px] text-gray-400 mt-0.5" data-testid={`connection-time-${c.id}`}>
                        Connected {new Date(c.created_at).toLocaleString()}
                      </p>
                    )}
                    {c.description && <p className="text-xs text-gray-600 mt-1">{c.description}</p>}
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`connection-adapter-${c.id}`}>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                        {accessLabel}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                        {authLabel}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                        {responseLabel}
                      </span>
                      {c.items_path && (
                        <span className="max-w-[240px] truncate rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                          Items: {c.items_path}
                        </span>
                      )}
                    </div>
                    <div
                      className="mt-1.5 flex flex-wrap items-center gap-1"
                      data-testid={`connection-geographic-scope-${c.id}`}
                    >
                      <span className="mr-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-400">Geographic coverage</span>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                        geographicScope === 'worldwide'
                          ? 'border-sky-100 bg-sky-50 text-sky-700'
                          : 'border-slate-200 bg-slate-50 text-slate-700'
                      }`}>
                        {geographicScope === 'worldwide'
                          ? <Globe className="h-3 w-3" />
                          : <MapPin className="h-3 w-3" />}
                        {geographicScope === 'worldwide' ? 'Worldwide' : 'Operations Area'}
                      </span>
                    </div>
                    {(coverage.all || coverage.hazards.length > 0) ? (
                      <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`connection-hazards-${c.id}`}>
                        <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 mr-0.5">Hazard coverage</span>
                        {coverage.all && (
                          <span className="rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[10px] font-semibold px-2 py-0.5">
                            All {HAZARD_FILTERS.length} hazards
                          </span>
                        )}
                        {coverage.hazards.map((hazard) => (
                          <span key={hazard.id} className="rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[10px] font-semibold px-2 py-0.5">
                            {hazard.icon} {hazard.label}
                          </span>
                        ))}
                        {coverage.inferred && (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-bold text-amber-800">
                            Legacy inference · review
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`connection-hazards-${c.id}`}>
                        <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400 mr-0.5">Hazard coverage</span>
                        <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-800">
                          No coverage declared
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {(
                      (c.purposes || []).includes('incident_reporting')
                      || c.foresight_enabled === true
                      || Boolean(c.hazard_coverage)
                    ) && c.is_active !== false && (
                      <button
                        data-testid={`fetch-events-${c.id}`}
                        onClick={() => fetchEvents(c)}
                        disabled={fetchingId === c.id}
                        className="flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-1.5 hover:bg-indigo-100 disabled:opacity-60"
                        title="Poll this source for new hazard observations"
                      >
                        {fetchingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rss className="h-3.5 w-3.5" />}
                        Poll now
                      </button>
                    )}
                    <button
                      data-testid={`test-api-connection-${c.id}`}
                      onClick={() => test(c)}
                      disabled={testingId === c.id}
                      className="flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1.5 hover:bg-emerald-100 disabled:opacity-60"
                    >
                      {testingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                      Test
                    </button>
                    <button data-testid={`edit-api-connection-${c.id}`} onClick={() => setDraft(normalizeConnectionDraft(c))}
                      className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-indigo-600" title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`delete-api-connection-${c.id}`} onClick={() => remove(c)}
                      className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-rose-600" title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {draft && <ConnectionFormModal draft={draft} onSave={save} onClose={() => setDraft(null)} onTest={test} />}
      {eventsModal && (
        <FeedEventsModal
          connection={eventsModal.connection}
          events={eventsModal.events}
          onClose={() => setEventsModal(null)}
        />
      )}
    </div>
  );
};

export default ApiConnections;
